import { z } from 'zod';
const TABLE_TYPES = ['ROUND', 'OVAL', 'SQUARE', 'RECTANGULAR', 'HEAD'];
const ROOM_WALLS = ['north', 'south', 'east', 'west'];
export const createFloorPlanSchema = z.object({
    eventId: z.string().uuid().optional(),
    roomId: z.string().uuid().optional(),
    name: z.string().min(1).max(255),
    width: z.number().int().min(100).max(10000).optional(),
    height: z.number().int().min(100).max(10000).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
});
export const updateFloorPlanSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    eventId: z.string().uuid().nullable().optional(),
    roomId: z.string().uuid().nullable().optional(),
    width: z.number().int().min(100).max(10000).optional(),
    height: z.number().int().min(100).max(10000).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).nullable().optional(),
});
export const assignGuestsToTableSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(50),
    tableNumber: z.number().int().min(1),
});
// ─── Floor Plan v2 — Room & Object schemas ──────────────────────────────────
const perimeterPointSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export const createRoomSchema = z.object({
    venueId: z.string().uuid(),
    name: z.string().min(1).max(255),
    roomType: z.string().max(100).optional(),
    widthFt: z.number().min(1).max(10000),
    heightFt: z.number().min(1).max(10000),
    maxCapacity: z.number().int().min(1).max(100000).optional(),
    perimeterPoints: z.array(perimeterPointSchema).min(3).optional(),
});
export const updateRoomSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    roomType: z.string().max(100).nullable().optional(),
    widthFt: z.number().min(1).max(10000).optional(),
    heightFt: z.number().min(1).max(10000).optional(),
    maxCapacity: z.number().int().min(1).max(100000).nullable().optional(),
    perimeterPoints: z.array(perimeterPointSchema).min(3).nullable().optional(),
});
export const duplicateFloorPlanSchema = z.object({
    targetEventId: z.string().uuid(),
    name: z.string().min(1).max(255).optional(),
});
// ─── Floor Plan Object Type Config Schemas ──────────────────────────────────
const OBJECT_SHAPES = [
    'RECTANGLE',
    'CIRCLE',
    'OVAL',
    'ROUNDED_RECT',
    'DIAMOND',
    'HEXAGON',
    'SERPENTINE',
];
export const createFloorPlanObjectTypeSchema = z.object({
    name: z.string().min(1).max(255),
    category: z.string().min(1).max(100),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    defaultColor: z.string().max(20).optional(),
    defaultSeats: z.number().int().min(0).max(500).optional(),
    icon: z.string().max(100).optional(),
    shape: z.enum(OBJECT_SHAPES).optional(),
    strokeColor: z.string().max(20).optional(),
    canHaveChairs: z.boolean().optional(),
    chairSizeFt: z.number().min(0.1).max(10).optional(),
    isTable: z.boolean().optional(),
    isBackground: z.boolean().optional(),
    aspectRatioLocked: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const updateFloorPlanObjectTypeSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    category: z.string().min(1).max(100).optional(),
    widthFt: z.number().min(0.1).max(1000).optional(),
    heightFt: z.number().min(0.1).max(1000).optional(),
    defaultColor: z.string().max(20).optional(),
    defaultSeats: z.number().int().min(0).max(500).nullable().optional(),
    icon: z.string().max(100).nullable().optional(),
    shape: z.enum(OBJECT_SHAPES).optional(),
    strokeColor: z.string().max(20).nullable().optional(),
    canHaveChairs: z.boolean().optional(),
    chairSizeFt: z.number().min(0.1).max(10).nullable().optional(),
    isTable: z.boolean().optional(),
    isBackground: z.boolean().optional(),
    aspectRatioLocked: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const createRoomEntrySchema = z.object({
    name: z.string().max(100).optional(),
    wall: z.enum(ROOM_WALLS),
    position: z.number().min(0).max(1),
    widthFt: z.number().min(1).max(100).optional(),
});
export const createFloorPlanObjectSchema = z.object({
    objectType: z.string().min(1).max(50),
    label: z.string().max(255).optional(),
    x: z.number(),
    y: z.number(),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).optional(),
    /**
     * Set when the object is a seatable table (legacy round/oval/etc. or a
     * custom-table object from the Settings library). Null for background
     * art, bars, stages, etc.
     */
    tableNumber: z.number().int().min(0).optional(),
    config: z.record(z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
});
export const updateFloorPlanObjectSchema = z.object({
    label: z.string().max(255).nullable().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
    widthFt: z.number().min(0.1).max(1000).optional(),
    heightFt: z.number().min(0.1).max(1000).optional(),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).nullable().optional(),
    tableNumber: z.number().int().min(0).nullable().optional(),
    config: z.record(z.unknown()).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
const objectUpsertItem = z.object({
    id: z.string().uuid().optional(),
    objectType: z.string().min(1).max(50),
    label: z.string().max(255).optional(),
    x: z.number(),
    y: z.number(),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).optional(),
    tableNumber: z.number().int().min(0).nullable().optional(),
    config: z.record(z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
});
export const bulkUpsertObjectsSchema = z.object({
    objects: z.array(objectUpsertItem).min(0).max(500),
});
// ─── Auto-assign seating ────────────────────────────────────────────────
export const SEATING_STRATEGIES = [
    'BY_GROUP',
    'BY_SIDE',
    'SEPARATE_MF',
    'KIDS_WITH_ADULTS',
    'KIDS_SEPARATE',
];
export const autoAssignSeatsSchema = z.object({
    strategy: z.enum(SEATING_STRATEGIES),
    /**
     * Default value applied when a guest's per-row `keepTogether` is null.
     * When the per-guest field is true/false it always overrides this default.
     */
    respectKeepTogether: z.boolean().default(true),
    /** When true, returns the proposed plan + shortfall without persisting. */
    dryRun: z.boolean().default(false),
    /**
     * If present and there is a shortfall, the algorithm creates extra tables
     * with the given seats-per-table to absorb it, then re-runs the placement.
     */
    autoAddTables: z
        .object({
        seatsPerTable: z.number().int().min(2).max(50),
        tableType: z.enum(TABLE_TYPES).default('ROUND'),
    })
        .optional(),
});
export const aiSeatingSchema = z.object({
    /** When true, returns the AI-proposed plan without persisting. */
    dryRun: z.boolean().default(false),
    /**
     * The reviewed arrangement, sent back on Apply so the persisted plan matches
     * the preview instead of a freshly (and non-deterministically) generated one.
     * Omitted on Preview. Re-validated server-side before persisting.
     */
    reviewedAssignments: z
        .array(z.object({ guestId: z.string(), tableNumber: z.number().int() }))
        .optional(),
});
//# sourceMappingURL=floor-plan.js.map